export interface AgentFileValidationResult {
	acceptedFiles: File[];
	errors: string[];
}

export const codeInterpreterFileExtensions = [
	'.c',
	'.cs',
	'.cpp',
	'.csv',
	'.doc',
	'.docx',
	'.html',
	'.java',
	'.json',
	'.md',
	'.pdf',
	'.php',
	'.pptx',
	'.py',
	'.rb',
	'.tex',
	'.txt',
	'.css',
	'.js',
	'.sh',
	'.ts',
	'.jpeg',
	'.jpg',
	'.gif',
	'.pkl',
	'.png',
	'.tar',
	'.xlsx',
	'.xml',
	'.zip',
];

export const fileSearchFileExtensions = [
	'.c',
	'.cpp',
	'.cs',
	'.css',
	'.doc',
	'.docx',
	'.go',
	'.html',
	'.java',
	'.js',
	'.json',
	'.md',
	'.pdf',
	'.php',
	'.pptx',
	'.py',
	'.rb',
	'.sh',
	'.tex',
	'.ts',
	'.txt',
];

const textLikeExtensions = new Set([
	'.c',
	'.cpp',
	'.cs',
	'.css',
	'.csv',
	'.go',
	'.html',
	'.java',
	'.js',
	'.json',
	'.md',
	'.php',
	'.py',
	'.rb',
	'.sh',
	'.tex',
	'.ts',
	'.txt',
	'.xml',
]);

const getExtension = (fileName: string): string => {
	const dotIndex = fileName.lastIndexOf('.');
	if (dotIndex < 0) {
		return '';
	}

	return fileName.slice(dotIndex).toLowerCase();
};

const removeLeadingControlCharacters = (content: string): string => {
	let index = 0;
	while (index < content.length && content.charCodeAt(index) < 32) {
		index += 1;
	}

	return content.slice(index);
};

export const sanitizeFileName = (fileName: string): string => {
	return fileName.replace(/[';=]/g, '');
};

export const sanitizeTextContent = (content: string): string => {
	const withoutLeadingControlChars = removeLeadingControlCharacters(content);
	return withoutLeadingControlChars.replace(/[\x00-\x08\x0B\x0C\x0E-\x1F\x7F]/g, '');
};

export const getAcceptedAgentUploadExtensions = (
	enableCodeInterpreter: boolean,
	enableFileSearch: boolean,
): string[] => {
	const accepted = new Set<string>();

	if (enableCodeInterpreter) {
		codeInterpreterFileExtensions.forEach((extension) => accepted.add(extension));
	}

	if (enableFileSearch) {
		fileSearchFileExtensions.forEach((extension) => accepted.add(extension));
	}

	return [...accepted];
};

const sanitizeTextFileIfNeeded = async (file: File): Promise<File> => {
	const extension = getExtension(file.name);

	if (!textLikeExtensions.has(extension)) {
		return file;
	}

	const content = await file.text();
	const sanitizedContent = sanitizeTextContent(content);
	const sanitizedFileName = sanitizeFileName(file.name) || file.name;

	return new File([sanitizedContent], sanitizedFileName, {
		type: file.type,
		lastModified: file.lastModified,
	});
};

export const validateAndSanitizeAgentUploadFiles = async (
	files: File[],
	options: {
		currentCount: number;
		maxFileCount: number;
		maxFileSizeMb: number;
		acceptedExtensions: string[];
	},
): Promise<AgentFileValidationResult> => {
	const acceptedFiles: File[] = [];
	const errors: string[] = [];
	const acceptedSet = new Set(options.acceptedExtensions.map((extension) => extension.toLowerCase()));
	const maxBytes = options.maxFileSizeMb * 1024 * 1024;
	let runningCount = options.currentCount;

	for (const file of files) {
		if (runningCount >= options.maxFileCount) {
			errors.push(`Reached max file count of ${options.maxFileCount}.`);
			break;
		}

		const extension = getExtension(file.name);
		if (acceptedSet.size > 0 && !acceptedSet.has(extension)) {
			errors.push(`File type not allowed: ${file.name}`);
			continue;
		}

		if (file.size > maxBytes) {
			errors.push(`File exceeds ${options.maxFileSizeMb}MB: ${file.name}`);
			continue;
		}

		const sanitizedFileName = sanitizeFileName(file.name);
		const renamedFile = sanitizedFileName && sanitizedFileName !== file.name
			? new File([file], sanitizedFileName, {
				type: file.type,
				lastModified: file.lastModified,
			})
			: file;

		const sanitizedFile = await sanitizeTextFileIfNeeded(renamedFile);
		acceptedFiles.push(sanitizedFile);
		runningCount += 1;
	}

	return { acceptedFiles, errors };
};

export const handleDuplicate = (existingFileNames: Set<string>, fileName: string, filesArray: File[]): string => {
	const splitName = fileName.split('.');
	const baseName = splitName.slice(0, splitName.length - 1).join('.');
	const extension = splitName[splitName.length - 1];

	let counter = 0;
	let newName = fileName;
	while (existingFileNames.has(newName) || filesArray.find((f) => f.name === newName)) {
		counter++;
		newName = `${baseName}_${counter}.${extension}`;
	}
	return newName;
};
